import { Component, Input, Output, EventEmitter, TemplateRef, OnInit, OnChanges, AfterViewInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, SimpleChanges } from '@angular/core';
import { formatDate as formatDateUtil } from '../../../core/utils/date.util';

/**
 * Table component
 *
 * Notes:
 * - Implements lightweight virtualization to reduce DOM and paint cost for large tables.
 * - Uses a small row-height approximation and top/bottom spacers so the browser can scroll normally.
 * - When client-side pagination is active the component renders the full paginated slice instead
 *   of virtualizing (so selecting a page-size like 50/100 still renders all rows on that page).
 * - Cell value formatting is intentionally pre-computed where possible and cached in _cellValueCache
 *   to avoid repeated formatter invocations during Angular's change-detection cycles.
 *
 * The comments above record the non-obvious trade-offs for future maintainers.
 */
export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  /** Column header shows filter dropdown with unique values and multi-select checkboxes.
   *  Enabled by default; set to false to hide. */
  filterable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  /** Optional Font Awesome classes for header icon (e.g. 'fas fa-user-md') */
  headerIcon?: string;
  cellTemplate?: TemplateRef<any>;
  headerTemplate?: TemplateRef<any>;
  formatter?: (value: any, row: any) => string;
  /** Extra classes on the inner cell span (e.g. `text-red-600` for negative amounts). */
  cellClass?: (row: any, column: TableColumn) => string | undefined | null;
  /** Extra classes on the footer cell span; `rawValue` is `footerRow[column.key]` before display formatting. */
  footerCellClass?: (column: TableColumn, rawValue: any) => string | undefined | null;
}

export interface ActionMenuItem {
  label: string;
  icon?: string;
  action: string;
  disabled?: (row: any) => boolean;
  divider?: boolean;
}

export interface TableConfig {
  columns: TableColumn[];
  data: any[];
  striped?: boolean;
  hoverable?: boolean;
  bordered?: boolean;
  selectable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  currentPage?: number;
  totalRecords?: number;
  serverSidePagination?: boolean;
  emptyMessage?: string;
  headerClass?: string;
  /** Extra classes on each body row (e.g. error/warning styling). */
  rowClassResolver?: (row: any, index: number) => string | undefined;
}

@Component({
  selector: 'app-table',
  standalone: false,
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() config?: TableConfig;
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  /** Optional additional header row (e.g. grouped bands with colspan) rendered above the standard column headers. */
  @Input() groupHeaderTemplate?: TemplateRef<any>;
  @Input() striped: boolean = true;
  @Input() hoverable: boolean = true;
  @Input() bordered: boolean = false;
  @Input() selectable: boolean = false;
  @Input() pagination: boolean = false;
  @Input() pageSize: number = 50;
  @Input() emptyMessage: string = 'No data available';
  @Input() serverSidePagination: boolean = false;
  @Input() actionMenuItems: ((row: any) => ActionMenuItem[]) | null = null;
  @Input() showActionsColumn: boolean = false;
  /** Align action buttons within the actions column (default matches existing tables). */
  @Input() actionsColumnAlign: 'center' | 'right' = 'center';
  /** Optional Font Awesome classes for Actions column header icon (e.g. 'fas fa-cog') */
  @Input() actionsHeaderIcon?: string;
  @Input() useKebabMenu: boolean = false;
  @Input() onViewDetails?: (row: any) => void;
  @Input() onDelete?: (row: any) => void;
  @Input() onHistory?: (row: any) => void;
  /** When true, right-click on rows does nothing (no context menu, browser menu suppressed). */
  @Input() disableContextMenu: boolean = false;
  /** Optional footer row: object keyed by column key. Numeric values are right-aligned and bold. */
  @Input() footerRow: Record<string, any> | null = null;
  /**
   * When true (and `footerRow` is set), footer cells use `position: sticky; bottom: 0` inside the nearest
   * scrollable ancestor so the summary row stays visible while body rows scroll.
   */
  @Input() stickyFooter = false;
  /** Optional footer template to render in tfoot section */
  @Input() footerTemplate?: TemplateRef<any>;
  /** Optional predicate: when true, row is rendered as a single full-width row (colspan). */
  @Input() isFullWidthRow?: (row: any) => boolean;
  /** Template for full-width rows. Context: { $implicit: row, row: row, colspan: bodyColSpan }. */
  @Input() fullWidthRowTemplate?: TemplateRef<any>;
  /** Optional per-cell style (e.g. backgroundColor for Account# from patientStatusColor). */
  @Input() getCellStyle?: (row: any, column: TableColumn) => { [key: string]: string } | undefined;
  /** Optional per-row style resolver (used when a screen needs explicit row background precedence). */
  @Input() rowStyleResolver?: (row: any) => { [key: string]: string } | undefined;
  /** When true, header row shows a dark bottom border (underline) below column headers. */
  @Input() headerDarkUnderline: boolean = false;
  /** When true, row underlines (borders between rows) use a darker, more visible style. */
  @Input() darkRowBorders: boolean = false;
  /**
   * Increment from the parent when the page filter panel is reset so column header filters clear too.
   * Bind: `[headerFiltersResetKey]="tableHeaderFiltersResetKey"` and run `tableHeaderFiltersResetKey++` in `onFilterReset`.
   */
  @Input() headerFiltersResetKey = 0;
  /** Optional storage key to persist table header filters/sort in localStorage. */
  @Input() headerFiltersStorageKey: string | null = null;

  /** Controls font weight for table body/list content (rows, empty state, footer). */
  @Input() bodyFontWeight: 'normal' | 'semibold' | 'bold' = 'semibold';
  /** Base text color class for non-status rows (e.g. `text-black`, `text-slate-800`). */
  @Input() bodyTextColorClass: string = 'text-slate-800';
  /** When true, status-colored rows can override text color for contrast (white/blue). */
  @Input() useStatusContrastText: boolean = true;
  /** Optional text color class for selected rows (e.g. `text-white`). */
  @Input() selectedRowTextColorClass: string | null = null;
  /** When true, selected row text color is derived from status (white only for specific statuses). */
  @Input() selectedRowStatusAwareText: boolean = false;

  /** Controls font weight for table header content (`th`). */
  @Input() headerFontWeight: 'normal' | 'semibold' | 'bold' = 'semibold';

  /** When true, `rowClick` emits `{ row, event }` so parents can detect Ctrl/Cmd multi-select. */
  @Input() rowClickIncludesEvent = false;
  @Output() rowClick = new EventEmitter<any>();
  @Output() rowDblclick = new EventEmitter<any>();
  @Output() rowSelect = new EventEmitter<any[]>();
  @Output() sort = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
  @Output() actionClick = new EventEmitter<{ action: string; row: any }>();
  @Output() delete = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<number>();
  /**
   * Emits the number of rows after applying the column header filters.
   * Useful for parent components that show "Showing X" counts above the table.
   */
  @Output() filteredRowCountChange = new EventEmitter<number>();
  /** Emits the row array after column header filters (same basis as filtered row count). Use for print/PDF export. */
  @Output() filteredRowsChange = new EventEmitter<any[]>();
  /** When true, first column is a drag handle; `rowReorder` emits indices into parent `data` (display order). */
  @Input() rowReorderable = false;
  @Output() rowReorder = new EventEmitter<{ fromIndex: number; toIndex: number }>();

  selectedRows: Set<any> = new Set();
  currentPage: number = 1;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | null = null; // null means no sort (reset state)
  contextMenuOpen: { row: any; x: number; y: number } | null = null;
  @ViewChild('tableContainer', { static: false }) tableContainer!: ElementRef;
  private virtualScrollEl: HTMLElement | null = null;
  private removeVirtualScrollListener: (() => void) | null = null;
  private _virtualScrollRaf = 0;

  /** Column filter: selected values per column (empty = show all) */
  columnFilters: Record<string, Set<string>> = {};
  columnFilterOpen: string | null = null;
  /** Snapshot of unique values for the open column filter — built once when opening the dropdown. */
  openColumnFilterUniqueValues: string[] = [];
  private columnFilterAnchorEl: HTMLElement | null = null;
  columnFilterDropdownStyle: Partial<CSSStyleDeclaration> | null = null;
  private removeRepositionListeners: (() => void) | null = null;
  @ViewChild('columnFilterPortal') columnFilterPortal?: ElementRef<HTMLElement>;

  /** When true, user can reorder columns via drag-and-drop in the header. */
  @Input() columnReorderable: boolean = true;
  /** Current column order by column key (only applies to `config.columns` / data columns). */
  private columnOrderKeys: string[] | null = null;
  draggingColumnKey: string | null = null;
  columnReorderDragInProgress: boolean = false;
  private columnDragLastOverKey: string | null = null;
  private pendingMarkForCheck: boolean = false;
  private savedBodyCursor: string | null = null;
  private savedBodyUserSelect: string | null = null;
  private dragImageEl: HTMLElement | null = null;

  /** HTML5 row reorder: render index of row being dragged (for highlight). */
  rowDragSourceRenderIndex: number | null = null;
  /** Render index of row currently under drag cursor. */
  rowDragOverRenderIndex: number | null = null;

  constructor(private cdr: ChangeDetectorRef) { }

  /** Total extra columns before data columns (grip + checkbox). */
  get rowReorderColumnCount(): number {
    return this.rowReorderable ? 1 : 0;
  }

  /** Colspan for empty / virtual spacer rows. */
  get bodyColSpan(): number {
    return (
      this.displayColumns.length +
      (this.isSelectable ? 1 : 0) +
      (this.showActionsColumn ? 1 : 0) +
      this.rowReorderColumnCount
    );
  }

  get bodyFontWeightClass(): string {
    switch (this.bodyFontWeight) {
      case 'normal':
        return 'font-normal';
      case 'bold':
        return 'font-bold';
      case 'semibold':
      default:
        return 'font-semibold';
    }
  }

  get headerFontWeightClass(): string {
    switch (this.headerFontWeight) {
      case 'normal':
        return 'font-normal';
      case 'bold':
        return 'font-bold';
      case 'semibold':
      default:
        return 'font-semibold';
    }
  }
  // Virtualization support
  @Input() virtualize: boolean = true;
  @Input() virtualThreshold: number = 50; // enable virtualization when rows exceed this
  /** Override estimated row height (px) for virtual padding; use ~34 for compact report tables. */
  @Input() virtualRowHeight?: number;
  /** Extra rows rendered above/below the viewport slice (default 10). Increase for smoother fast scroll on heavy rows. */
  @Input() virtualBuffer: number = 10;
  /** When set (e.g. '70vh'), the table container becomes the vertical scroll host for virtualization. */
  @Input() scrollMaxHeight?: string;
  rowHeight: number = 44; // px, approximate row height used for virtualization
  virtualEnabled: boolean = false;
  visibleRows: any[] = [];
  topPadding = 0;
  bottomPadding = 0;
  private _lastVirtualSliceStart = -1;
  private _lastVirtualSliceEnd = -1;
  private _lastVirtualTopPadding = -1;
  private _lastVirtualBottomPadding = -1;
  /** Cached render source for virtual slice — avoids re-running getters on every scroll event. */
  private _virtualRenderSourceRef: any[] | null = null;
  private _virtualRenderSource: any[] = [];
  // Internal full dataset used for virtualization; kept in sync with displayData/sortedData
  tableData: any[] = [];
  // Cache computed cell values per row to avoid expensive recomputation during template checks
  private _cellValueCache: WeakMap<any, Record<string, any>> = new WeakMap();
  // Cache normalized string values used by header filtering (separate from display cache for speed)
  private _filterValueCache: WeakMap<any, Record<string, string>> = new WeakMap();
  // Memoization for sorting to avoid repeated sorts during CD cycles
  private _lastDataRef: any = null;
  private _lastSortColumn: string = '';
  private _lastSortDirection: 'asc' | 'desc' | null = null;
  private _sortedCache: any[] | null = null;

  /** Cache of the latest header-filtered rows, for footer totals recalculation. */
  private _cachedFilteredDisplayData: any[] = [];
  /** Cache of computed numeric footer sums for the current header-filter state. */
  private _footerSumCache: Record<string, number> | null = null;
  /** Cache unique column values for header filter dropdown (avoid O(n) recompute on every CD). */
  private _uniqueValuesCacheDataRef: any[] | null = null;
  private _uniqueValuesCache = new Map<string, string[]>();
  /** Cache filteredDisplayData to avoid O(n) filtering on every CD cycle. */
  private _filteredDisplayCacheDataRef: any[] | null = null;
  private _filteredDisplayCacheKey: string | null = null;
  private _filteredDisplayCache: any[] | null = null;
  /** Sorted full dataset cache — restoring header filters reuses this instead of re-sorting O(n log n). */
  private _masterSortedCache: any[] | null = null;
  private _masterSortedCacheDataRef: any[] | null = null;
  private _masterSortedCacheSortKey: string | null = null;
  private _emitFilteredRaf = 0;
  private _emitFilteredToken = 0;
  private _persistFilterTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (this.config?.currentPage) {
      this.currentPage = this.config.currentPage;
    }
    this.restoreHeaderFilterState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['headerFiltersResetKey'] && !changes['headerFiltersResetKey'].firstChange) {
      this.clearAllColumnFilters();
    }
    if (this.config?.currentPage !== undefined && this.config.currentPage !== this.currentPage) {
      this.currentPage = this.config.currentPage;
    }
    if (this.config?.pageSize && this.config.pageSize !== this.displayPageSize) {
      this.currentPage = 1;
    }
    // Clear cached cell values when inputs change to avoid stale values
    try { this._cellValueCache = new WeakMap(); } catch {}
    try { this._filterValueCache = new WeakMap(); } catch {}
    // Clear sort cache when data changes to ensure fresh sorting
    this._sortedCache = null;
    this._lastDataRef = null;
    this._masterSortedCache = null;
    this._masterSortedCacheDataRef = null;
    this._masterSortedCacheSortKey = null;
    // reset virtualization when config changes
    this.virtualEnabled = false;
    this.visibleRows = [];
    this.topPadding = 0;
    this.bottomPadding = 0;
    this._virtualRenderSourceRef = null;
    this._virtualRenderSource = [];

    // Clear unique-values cache whenever inputs change (safe + keeps dropdown fast).
    this._uniqueValuesCacheDataRef = null;
    this._uniqueValuesCache.clear();
    // Clear filtered-display cache whenever inputs change.
    this._filteredDisplayCacheDataRef = null;
    this._filteredDisplayCacheKey = null;
    this._filteredDisplayCache = null;

    const sourceArray = this.getEffectiveRenderSource();

    // Keep internal tableData in sync (used for legacy progressive rendering)
    this.tableData = Array.isArray(sourceArray) ? sourceArray : [];

    this.recomputeVirtualEnabled();
    if (this.virtualEnabled) {
      this.resetVirtualSliceCache();
      // Initialize/update visible rows at the current scroll position (avoid "jump to top" feel after filtering).
      const scrollEl = this.virtualScrollEl ?? (this.tableContainer?.nativeElement as HTMLElement | undefined);
      const scrollTop = scrollEl?.scrollTop ?? 0;
      if (this.updateVirtualRange(scrollTop)) {
        this.cdr.detectChanges();
      }
    }
    this.cdr.markForCheck();

    // If the table itself isn't the scroll container (page-level/outer div scroll), virtualization must
    // listen to the nearest scroll parent. Defer until view is stable so #tableContainer exists.
    Promise.resolve().then(() => {
      this.attachVirtualScrollListener();
      this.refreshVirtualRangeAfterAttach();
    });

    // Keep header filter selections valid for the latest dataset.
    // If a selected value no longer exists in refreshed data, drop it so the table doesn't stay empty
    // after backend-filter changes or navigation restores.
    this.pruneColumnFiltersForCurrentData();
    this.pruneStaleSelectedRows();

    if (this.columnFilterOpen) {
      const col = this.displayColumns.find(c => c.key === this.columnFilterOpen);
      this.openColumnFilterUniqueValues = col ? this.getUniqueValuesForColumn(col) : [];
    }

    // Notify parent of the filtered row count (so any external count bar updates).
    this.emitFilteredRowCount();

    // OnPush: ensure tbody updates when parent replaces `config` / `data` (avoids stale grid until next interaction).
    if (changes['data'] || changes['config']) {
      Promise.resolve().then(() => {
        try {
          this.cdr.detectChanges();
        } catch {
          /* ignore if already in unstable CD */
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.attachVirtualScrollListener();
    // Capture phase: modal shells often stopPropagation on bubble, which blocked bubble-phase dismiss.
    document.addEventListener('click', this.onDocumentClickCapture, true);
  }

  /** When the scroll container is a parent of the table (not #tableContainer), wire scroll from the page. */
  handleExternalScroll(scrollTop: number): void {
    if (!this.virtualEnabled) return;
    if (this.updateVirtualRange(scrollTop)) {
      this.cdr.detectChanges();
    }
  }

  /** Re-attach scroll listener and refresh visible slice (call after large data loads). */
  refreshVirtualScroll(): void {
    this.attachVirtualScrollListener();
    this.refreshVirtualRangeAfterAttach();
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onDocumentClickCapture, true);
    try { this.removeVirtualScrollListener?.(); } catch {}
    this.removeVirtualScrollListener = null;
    try { this.removeRepositionListeners?.(); } catch {}
    this.removeRepositionListeners = null;
    this.detachColumnFilterPortalFromBody();
    if (this._emitFilteredRaf) cancelAnimationFrame(this._emitFilteredRaf);
    this._emitFilteredRaf = 0;
    if (this._virtualScrollRaf) cancelAnimationFrame(this._virtualScrollRaf);
    this._virtualScrollRaf = 0;
  }

  private attachVirtualScrollListener(): void {
    try {
      this.removeVirtualScrollListener?.();
    } catch {}
    this.removeVirtualScrollListener = null;

    const base = this.tableContainer?.nativeElement as HTMLElement | undefined;
    if (!base) return;

    const target = this.resolveVirtualScrollElement(base);
    this.virtualScrollEl = target;

    try {
      const onScroll = () => this.onContainerScroll();
      target.addEventListener('scroll', onScroll, { passive: true });
      this.removeVirtualScrollListener = () => target.removeEventListener('scroll', onScroll);
      this.refreshVirtualRangeAfterAttach();
    } catch {
      this.removeVirtualScrollListener = null;
    }
  }

  private refreshVirtualRangeAfterAttach(): void {
    if (!this.virtualEnabled) return;
    const apply = () => {
      const el = this.virtualScrollEl ?? (this.tableContainer?.nativeElement as HTMLElement | undefined);
      if (this.updateVirtualRange(el?.scrollTop ?? 0)) {
        this.cdr.detectChanges();
      }
    };
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(apply);
    } else {
      apply();
    }
  }

  private resolveVirtualScrollElement(base: HTMLElement): HTMLElement {
    // When scrollMaxHeight is set, #tableContainer is always the scroll host.
    if (this.scrollMaxHeight) return base;

    // If the internal container scrolls vertically, use it.
    if (base.scrollHeight > base.clientHeight) return base;

    // Prefer the nearest ancestor marked for vertical scrolling even before content
    // is laid out (large datasets attach the listener before rows fill the viewport).
    let el: HTMLElement | null = base.parentElement;
    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return el;
      }
      el = el.parentElement;
    }
    return base;
  }

  get displayColumns(): TableColumn[] {
    const baseColumns: TableColumn[] = this.config?.columns || this.columns;
    if (!this.columnReorderable) return baseColumns;

    // Keep column order in sync if column set changes (e.g. different page config).
    const baseKeys = baseColumns.map(c => c.key);
    if (!this.columnOrderKeys) {
      this.columnOrderKeys = [...baseKeys];
    } else {
      // Reset when the set/length of columns changes.
      const sameLength = this.columnOrderKeys.length === baseKeys.length;
      const sameSet = baseKeys.every(k => this.columnOrderKeys!.includes(k));
      if (!sameLength || !sameSet) {
        this.columnOrderKeys = [...baseKeys];
      } else {
        // Append any newly added keys at the end (defensive).
        const missing = baseKeys.filter(k => !this.columnOrderKeys!.includes(k));
        if (missing.length) {
          this.columnOrderKeys = [...this.columnOrderKeys, ...missing];
        }
      }
    }

    const keyToColumn = new Map<string, TableColumn>();
    baseColumns.forEach(c => keyToColumn.set(c.key, c));
    if (!this.columnOrderKeys?.length) return baseColumns;

    const ordered: TableColumn[] = this.columnOrderKeys
      .map(k => keyToColumn.get(k))
      .filter((c): c is TableColumn => !!c);

    // Safety: ensure no column disappears due to key mismatch.
    const remaining = baseColumns.filter(c => !this.columnOrderKeys!.includes(c.key));
    return [...ordered, ...remaining];
  }

  get displayData(): any[] {
    const d = this.config?.data || this.data;
    // Keep internal tableData up to date for virtualization
    try { this.tableData = Array.isArray(d) ? d : []; } catch { this.tableData = []; }
    return d;
  }

  /** Display data after applying column filters (multi-select per column) */
  get filteredDisplayData(): any[] {
    const data = this.displayData || [];
    // Column filter dropdown is enabled by default; only hide/disable when filterable === false.
    const cols = this.displayColumns.filter(c => c.filterable !== false && this.columnFilters[c.key]?.size > 0);
    if (cols.length === 0) return data;

    const key = this.buildColumnFiltersCacheKey(cols);
    if (this._filteredDisplayCacheDataRef === data && this._filteredDisplayCacheKey === key && this._filteredDisplayCache) {
      return this._filteredDisplayCache;
    }

    const filtered = data.filter(row => cols.every(col => {
      const val = this.getCellValueForFilter(row, col);
      return this.columnFilters[col.key].has(val);
    }));
    this._filteredDisplayCacheDataRef = data;
    this._filteredDisplayCacheKey = key;
    this._filteredDisplayCache = filtered;
    return filtered;
  }

  private buildColumnFiltersCacheKey(cols: TableColumn[]): string {
    // Stable string key: per column key => sorted selected values.
    // This is much cheaper than re-filtering the full dataset repeatedly.
    const parts: string[] = [];
    for (const col of cols) {
      const set = this.columnFilters[col.key];
      if (!set || set.size === 0) continue;
      const vals = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      parts.push(`${col.key}=${vals.join('|')}`);
    }
    parts.sort();
    return parts.join(';;');
  }

  get isStriped(): boolean {
    return this.config?.striped ?? this.striped;
  }

  get isHoverable(): boolean {
    return this.config?.hoverable ?? this.hoverable;
  }

  get isBordered(): boolean {
    return this.config?.bordered ?? this.bordered;
  }

  get isSelectable(): boolean {
    return this.config?.selectable ?? this.selectable;
  }

  get showPagination(): boolean {
    return this.config?.pagination ?? this.pagination;
  }

  get displayPageSize(): number {
    return this.config?.pageSize || this.pageSize;
  }

  get displayEmptyMessage(): string {
    return this.config?.emptyMessage || this.emptyMessage;
  }

  get paginatedData(): any[] {
    // If parent provided explicit pageSize/currentPage in config, respect that and return the slice.
    const data = this.sortedData;
    const pageSize = this.config?.pageSize ?? this.displayPageSize;
    const page = this.config?.currentPage ?? this.currentPage;

    if (!pageSize || pageSize <= 0) {
      return data;
    }

    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }

  /**
   * Rows to render in the template: visibleRows when virtualized,
   * otherwise either the paginated slice (client-side) or the full data (server-side: API already returned one page).
   */
  get renderRows(): any[] {
    if (this.virtualEnabled) return this.visibleRows;
    // Server-side pagination: data is already the current page from API; do not slice by pageSize.
    if (this.isServerSidePagination) return this.sortedData || this.displayData || [];
    // Always use sortedData when there's an active sort, otherwise use displayData
    // For paginated tables, paginatedData already uses sortedData internally
    return this.showPagination ? (this.paginatedData || []) : (this.sortedData || this.displayData || []);
  }

  get isServerSidePagination(): boolean {
    return this.config?.serverSidePagination ?? this.serverSidePagination;
  }

  get sortedData(): any[] {
    const data = this.filteredDisplayData || [];
    const fullData = this.displayData || [];
    const isFullUnfiltered = !this.hasActiveColumnFilters() && data === fullData;

    // If no sort requested or sort is reset, return data as-is (avoid copying when possible)
    if (!this.sortColumn || this.sortDirection === null) {
      // clear cached sort state
      this._sortedCache = null;
      this._lastDataRef = data;
      return data;
    }

    const sortKey = `${this.sortColumn}:${this.sortDirection}`;
    if (
      isFullUnfiltered &&
      this._masterSortedCache &&
      this._masterSortedCacheDataRef === fullData &&
      this._masterSortedCacheSortKey === sortKey
    ) {
      this._sortedCache = this._masterSortedCache;
      this._lastDataRef = data;
      this._lastSortColumn = this.sortColumn;
      this._lastSortDirection = this.sortDirection;
      return this._masterSortedCache;
    }

    // If data reference and sort settings unchanged, return cached sorted array
    if (this._sortedCache && this._lastDataRef === data && this._lastSortColumn === this.sortColumn && this._lastSortDirection === this.sortDirection) {
      return this._sortedCache;
    }

    // Find the column configuration to determine data type
    const column = this.displayColumns.find(col => col.key === this.sortColumn);
    const dataType = this.detectColumnDataType(column, data);

    /**
     * Group-band tables pass `isFullWidthRow` for header rows. Sorting the flat array mixes those
     * headers with data rows; instead sort only within each [optional anchor, …body] segment.
     */
    if (this.isFullWidthRow) {
      const segments = this.splitDataIntoFullWidthSegments(data);
      const out: any[] = [];
      for (const seg of segments) {
        // Keep group footers (subtotal/total rows) fixed after detail rows within each segment.
        const sortableRows = seg.body.filter((r) => !r?.rowType || r.rowType === 'data');
        const trailerRows = seg.body.filter((r) => r?.rowType && r.rowType !== 'data');
        const sortedBody = [...sortableRows].sort((a, b) => {
          const comparison = this.compareRowValuesForSort(a, b, dataType);
          return this.sortDirection === 'asc' ? comparison : -comparison;
        });
        if (seg.anchor != null) {
          out.push(seg.anchor);
        }
        out.push(...sortedBody, ...trailerRows);
      }
      this._sortedCache = out;
      this._lastDataRef = data;
      this._lastSortColumn = this.sortColumn;
      this._lastSortDirection = this.sortDirection;
      if (isFullUnfiltered) {
        this._masterSortedCache = out;
        this._masterSortedCacheDataRef = fullData;
        this._masterSortedCacheSortKey = sortKey;
      }
      return this._sortedCache;
    }

    // Compute sorted copy - create one copy to avoid modifying original
    const copy = [...data];
    copy.sort((a, b) => {
      const aVal = this.getNestedValue(a, this.sortColumn);
      const bVal = this.getNestedValue(b, this.sortColumn);

      // Handle null/undefined/empty values - always push to end
      const aIsEmpty = aVal === null || aVal === undefined || aVal === '';
      const bIsEmpty = bVal === null || bVal === undefined || bVal === '';
      
      if (aIsEmpty && bIsEmpty) return 0; // Both empty, maintain order
      if (aIsEmpty) return 1; // a is empty, push to end
      if (bIsEmpty) return -1; // b is empty, push to end

      let comparison = 0;

      switch (dataType) {
        case 'date':
          comparison = this.compareDates(aVal, bVal);
          break;
        case 'time':
          comparison = this.compareTimes(aVal, bVal);
          break;
        case 'number':
          comparison = this.compareNumbers(aVal, bVal);
          break;
        case 'string':
        default:
          comparison = this.compareStrings(aVal, bVal);
          break;
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    // Store cache and meta
    this._sortedCache = copy;
    this._lastDataRef = data;
    this._lastSortColumn = this.sortColumn;
    this._lastSortDirection = this.sortDirection;
    if (isFullUnfiltered) {
      this._masterSortedCache = copy;
      this._masterSortedCacheDataRef = fullData;
      this._masterSortedCacheSortKey = sortKey;
    }
    return this._sortedCache;
  }

  /**
   * Split table data into segments separated by full-width (e.g. group header) rows.
   * Each segment is one optional anchor row followed by normal rows until the next anchor.
   */
  private splitDataIntoFullWidthSegments(data: any[]): { anchor: any | null; body: any[] }[] {
    const segments: { anchor: any | null; body: any[] }[] = [];
    if (!data?.length || !this.isFullWidthRow) {
      return segments;
    }
    const isAnchor = this.isFullWidthRow;
    let i = 0;
    while (i < data.length) {
      const row = data[i];
      if (isAnchor(row)) {
        const anchor = row;
        i++;
        const body: any[] = [];
        while (i < data.length && !isAnchor(data[i])) {
          body.push(data[i]);
          i++;
        }
        segments.push({ anchor, body });
      } else {
        const body: any[] = [];
        while (i < data.length && !isAnchor(data[i])) {
          body.push(data[i]);
          i++;
        }
        segments.push({ anchor: null, body });
      }
    }
    return segments;
  }

  /** Compare two data rows by the active sort column (asc/desc applied by caller). */
  private compareRowValuesForSort(
    a: any,
    b: any,
    dataType: 'date' | 'time' | 'number' | 'string'
  ): number {
    const aVal = this.getNestedValue(a, this.sortColumn);
    const bVal = this.getNestedValue(b, this.sortColumn);
    const aIsEmpty = aVal === null || aVal === undefined || aVal === '';
    const bIsEmpty = bVal === null || bVal === undefined || bVal === '';
    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;
    switch (dataType) {
      case 'date':
        return this.compareDates(aVal, bVal);
      case 'time':
        return this.compareTimes(aVal, bVal);
      case 'number':
        return this.compareNumbers(aVal, bVal);
      case 'string':
      default:
        return this.compareStrings(aVal, bVal);
    }
  }

  get totalPages(): number {
    if (this.isServerSidePagination && this.config?.totalRecords) {
      return Math.ceil(this.config.totalRecords / this.displayPageSize);
    }
    return Math.ceil(this.displayData.length / this.displayPageSize);
  }

  get Math(): typeof Math {
    return Math;
  }

  getCellValue(row: any, column: TableColumn): any {
    if (!row) return '';

    // Fast path: if row contains precomputed display values for this column, use them.
    if (row._display && Object.prototype.hasOwnProperty.call(row._display, column.key)) {
      return row._display[column.key];
    }

    // Use cache to avoid recomputing values repeatedly during CD cycles
    let rowCache = this._cellValueCache.get(row);
    const cacheKey = column.key;
    if (rowCache && Object.prototype.hasOwnProperty.call(rowCache, cacheKey)) {
      return rowCache[cacheKey];
    }

    const raw = this.getNestedValue(row, column.key);
    let computed: any;
    if (column.formatter) {
      try {
        computed = column.formatter(raw, row);
      } catch {
        computed = raw;
      }
    } else if (this.isDateColumn(column.key) && raw) {
      computed = this.formatDate(raw);
    } else if (this.shouldFormatAsMoney(column, raw)) {
      computed = this.formatMoneyDisplay(raw);
    } else {
      computed = raw;
    }

    if (!rowCache) {
      rowCache = {};
      this._cellValueCache.set(row, rowCache);
    }
    rowCache[cacheKey] = computed;
    return computed;
  }

  /** Full cell text for `title` when the cell is truncated (narrow columns / resize). */
  getCellTooltip(row: any, column: TableColumn): string | null {
    const v = this.getCellValue(row, column);
    if (v == null || v === '') return null;
    const s = String(v).trim();
    return s.length > 0 ? s : null;
  }

  private isDateColumn(key: string): boolean {
    const dateColumns = [
      'date',
      'checkIn',
      'dob',
      'dos',
      'bookedDate',
      'enteredDate',
      'visitDate',
      'startDate',
      'endDate',
      'entryDate',
      'lastLetterDate',
      'nextLetterDate',
      'resultDate',
      'dateTimeOfResult',
      'appointmentdate',
      'datepaid',
      'paiddate'
    ];
    const normalized = key.toLowerCase();
    if (dateColumns.includes(normalized)) return true;
    // e.g. datePaid — must not be formatted as money (key contains "paid").
    return normalized.startsWith('date') || normalized.endsWith('date');
  }

  /**
   * Detects the data type of a column by analyzing sample data values.
   * Returns 'date', 'time', 'number', or 'string'.
   */
  private detectColumnDataType(column: TableColumn | undefined, data: any[]): 'date' | 'time' | 'number' | 'string' {
    if (!column || !data || data.length === 0) {
      return 'string';
    }

    // Check column key for known date/time patterns
    const key = column.key.toLowerCase();
    if (this.isDateColumn(key)) {
      return 'date';
    }
    if (key === 'time' || key.includes('time')) {
      return 'time';
    }

    // Analyze sample values from the data
    const sampleSize = Math.min(10, data.length);
    let dateCount = 0;
    let timeCount = 0;
    let numberCount = 0;
    let validSamples = 0;

    for (let i = 0; i < sampleSize; i++) {
      const value = this.getNestedValue(data[i], column.key);
      if (value === null || value === undefined || value === '') {
        continue;
      }
      validSamples++;

      // Check if it's a date
      if (this.isDateValue(value)) {
        dateCount++;
      }
      // Check if it's a time
      else if (this.isTimeValue(value)) {
        timeCount++;
      }
      // Check if it's a number
      else if (this.isNumericValue(value)) {
        numberCount++;
      }
    }

    // If we have valid samples, determine type by majority
    if (validSamples > 0) {
      if (dateCount > validSamples * 0.5) return 'date';
      if (timeCount > validSamples * 0.5) return 'time';
      if (numberCount > validSamples * 0.5) return 'number';
    }

    // Default to string
    return 'string';
  }

  /**
   * Checks if a value is a date (Date object or date string).
   */
  private isDateValue(value: any): boolean {
    if (value instanceof Date) return true;
    if (typeof value !== 'string') return false;
    
    // Common date patterns: YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, etc.
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}/, // ISO format
      /^\d{2}\/\d{2}\/\d{4}/, // MM/DD/YYYY or DD/MM/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{4}/, // M/D/YYYY
    ];
    
    return datePatterns.some(pattern => pattern.test(value)) || !isNaN(Date.parse(value));
  }

  /**
   * Checks if a value is a time (HH:MM or HH:MM:SS format).
   */
  private isTimeValue(value: any): boolean {
    if (typeof value !== 'string') return false;
    const timePattern = /^(\d{1,2}):(\d{2})(:(\d{2}))?(\s*(AM|PM|am|pm))?$/;
    return timePattern.test(value.trim());
  }

  /**
   * Checks if a value is numeric (number or numeric string).
   */
  private isNumericValue(value: any): boolean {
    if (typeof value === 'number') return !isNaN(value) && isFinite(value);
    if (typeof value !== 'string') return false;
    
    // Remove currency symbols and commas
    const cleaned = value.replace(/[$,\s]/g, '');
    return !isNaN(parseFloat(cleaned)) && isFinite(parseFloat(cleaned));
  }

  /**
   * Compares two date values.
   */
  private compareDates(a: any, b: any): number {
    const dateA = this.parseDate(a);
    const dateB = this.parseDate(b);
    
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return dateA.getTime() - dateB.getTime();
  }

  /**
   * Parses a date value into a Date object.
   */
  private parseDate(value: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  /**
   * Compares two time values (HH:MM or HH:MM:SS format).
   */
  private compareTimes(a: any, b: any): number {
    const timeA = this.parseTime(a);
    const timeB = this.parseTime(b);
    
    if (timeA === null && timeB === null) return 0;
    if (timeA === null) return 1;
    if (timeB === null) return -1;
    
    return timeA - timeB;
  }

  /**
   * Parses a time string into minutes since midnight for comparison.
   */
  private parseTime(value: any): number | null {
    if (typeof value !== 'string') return null;
    
    const timeStr = value.trim().toUpperCase();
    const timePattern = /^(\d{1,2}):(\d{2})(:(\d{2}))?(\s*(AM|PM))?$/;
    const match = timeStr.match(timePattern);
    
    if (!match) return null;
    
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[6];
    
    // Handle 12-hour format
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return hours * 60 + minutes;
  }

  /**
   * Compares two numeric values.
   */
  private compareNumbers(a: any, b: any): number {
    const numA = this.parseNumber(a);
    const numB = this.parseNumber(b);
    
    if (numA === null && numB === null) return 0;
    if (numA === null) return 1;
    if (numB === null) return -1;
    
    return numA - numB;
  }

  /**
   * Parses a value into a number, handling currency symbols and commas.
   */
  private parseNumber(value: any): number | null {
    if (typeof value === 'number') return isNaN(value) ? null : value;
    if (typeof value !== 'string') return null;
    
    // Remove currency symbols, commas, and whitespace
    const cleaned = value.replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Compares two string values using locale-aware comparison.
   */
  private compareStrings(a: any, b: any): number {
    const strA = String(a || '').toLowerCase();
    const strB = String(b || '').toLowerCase();
    return strA.localeCompare(strB);
  }

  private formatDate(dateString: string): string {
    // Use centralized date utility for consistent MM/dd/yyyy format
    return formatDateUtil(dateString) || dateString;
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  /** Value used for column filter comparison (display value as string) */
  getCellValueForFilter(row: any, column: TableColumn): string {
    if (!row || !column?.key) return '';
    let rowCache = this._filterValueCache.get(row);
    if (rowCache && Object.prototype.hasOwnProperty.call(rowCache, column.key)) {
      return rowCache[column.key];
    }

    const v = this.getCellValue(row, column);
    const normalized = v === null || v === undefined ? '' : String(v).trim();

    if (!rowCache) {
      rowCache = {};
      this._filterValueCache.set(row, rowCache);
    }
    rowCache[column.key] = normalized;
    return normalized;
  }

  /**
   * System rule: numeric columns are center-aligned across list tables.
   * This applies to header/body/footer align resolution.
   */
  getResolvedColumnAlign(column: TableColumn): 'left' | 'center' | 'right' {
    return this.isNumericColumn(column) ? 'center' : (column.align || 'left');
  }

  private isNumericColumn(column: TableColumn): boolean {
    const key = column?.key;
    if (!key) return false;
    const rows = this.displayData || [];
    const sampleCount = Math.min(rows.length, 20);
    let checked = 0;
    let numeric = 0;
    for (let i = 0; i < sampleCount; i++) {
      const value = this.getNestedValue(rows[i], key);
      if (value === null || value === undefined || value === '') continue;
      checked++;
      if (this.isNumericValue(value)) numeric++;
    }
    return checked > 0 && numeric >= Math.ceil(checked * 0.7);
  }

  /** Unique values for a filterable column (from current display data) */
  getUniqueValuesForColumn(column: TableColumn): string[] {
    const data = this.displayData || [];
    const key = column?.key ?? '';
    if (!key) return [];

    if (this._uniqueValuesCacheDataRef !== data) {
      this._uniqueValuesCacheDataRef = data;
      this._uniqueValuesCache.clear();
    }
    const cached = this._uniqueValuesCache.get(key);
    if (cached) return cached;

    const seen = new Set<string>();
    for (const row of data) {
      const v = this.getCellValueForFilter(row, column);
      if (v !== '') seen.add(v);
    }
    const values = Array.from(seen);
    // Large option lists can be expensive to locale-sort and hurt interaction smoothness.
    // Keep deterministic ordering for small/medium lists; keep insertion order for very large lists.
    if (values.length <= 800) {
      values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    this._uniqueValuesCache.set(key, values);
    return values;
  }

  isColumnFilterActive(column: TableColumn): boolean {
    return (this.columnFilters[column.key]?.size ?? 0) > 0;
  }

  /** When a column is actively sorted or its filter dropdown is open,
   * expand header label so it doesn't appear cut off.
   */
  isHeaderLabelExpanded(column: TableColumn): boolean {
    if (!column?.key) return false;
    const filterOpen = this.columnFilterOpen === column.key;
    const sortActive = this.sortColumn === column.key && this.sortDirection !== null;
    return filterOpen || sortActive;
  }

  isColumnFilterValueSelected(column: TableColumn, value: string): boolean {
    return this.columnFilters[column.key]?.has(value) ?? false;
  }

  toggleColumnFilter(column: TableColumn, value: string, $event?: Event): void {
    if ($event) $event.stopPropagation();
    if (!this.columnFilters[column.key]) this.columnFilters[column.key] = new Set();
    const s = this.columnFilters[column.key];
    if (s.has(value)) s.delete(value);
    else s.add(value);
    if (s.size === 0) delete this.columnFilters[column.key];
    this._sortedCache = null;
    this._filteredDisplayCacheDataRef = null;
    this._filteredDisplayCacheKey = null;
    this._filteredDisplayCache = null;
    this.schedulePersistHeaderFilterState();
    this.afterColumnFilterMutation();
  }

  private schedulePersistHeaderFilterState(): void {
    if (!this.headerFiltersStorageKey) return;
    if (this._persistFilterTimer) {
      clearTimeout(this._persistFilterTimer);
    }
    this._persistFilterTimer = setTimeout(() => {
      this._persistFilterTimer = null;
      this.persistHeaderFilterState();
    }, 150);
  }

  /** Recompute row slice + counts without blocking the main thread on very large pages. */
  private afterColumnFilterMutation(): void {
    const len = this.displayData?.length ?? 0;
    const clearingAll = !this.hasActiveColumnFilters();
    const applyUi = () => {
      try {
        const el = this.virtualScrollEl ?? (this.tableContainer?.nativeElement as HTMLElement | undefined);
        if (clearingAll && el && len >= this.virtualThreshold) {
          el.scrollTop = 0;
        }
        this.recomputeVirtualEnabled();
        const scrollTop = clearingAll ? 0 : (el?.scrollTop ?? 0);
        if (this.virtualEnabled) {
          this.updateVirtualRange(scrollTop);
        }
        this.emitFilteredRowCount();
        this.cdr.markForCheck();
      } catch {
        try { this.cdr.markForCheck(); } catch { /* ignore */ }
      }
    };
    // Removing filters should feel instant — do not defer with rAF.
    if (clearingAll || len < 200) {
      applyUi();
      return;
    }
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(applyUi);
    } else {
      applyUi();
    }
  }

  // -----------------------------
  // Column reorder (drag & drop)
  // -----------------------------
  onColumnDragStart(columnKey: string, event: DragEvent): void {
    if (!this.columnReorderable) return;
    if (!columnKey) return;
    const target = event.target as HTMLElement | null;
    // Avoid starting drag when user interacts with resize handle.
    if (target?.closest?.('.table-column-resize-handle')) {
      event.preventDefault();
      return;
    }
    // Avoid drag when interacting with header controls (filter dropdown toggles / inputs).
    if (target?.closest?.('button, input, a, label')) return;

    this.draggingColumnKey = columnKey;
    this.columnReorderDragInProgress = true;
    this.columnDragLastOverKey = columnKey;

    // Improve drag feel: make the cursor indicate "move a column".
    // Save old values to restore on drag end/drop.
    try {
      const body = document.body;
      this.savedBodyCursor = body.style.cursor;
      this.savedBodyUserSelect = body.style.userSelect;
      body.style.cursor = 'col-resize';
      body.style.userSelect = 'none';
    } catch {
      // ignore if document.body is not accessible (SSR)
    }
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', columnKey);
    }

    // Provide a drag "ghost" so it feels like a whole column is being dragged.
    // Keep it lightweight: just mirror the header cell text.
    try {
      if (event.dataTransfer && typeof event.dataTransfer.setDragImage === 'function') {
        const th = target?.closest?.('th') as HTMLElement | null;
        if (th) {
          this.cleanupDragImage();
          const el = document.createElement('div');
          el.textContent = (th.innerText || '').trim();
          el.style.position = 'fixed';
          el.style.top = '-1000px';
          el.style.left = '-1000px';
          el.style.padding = '8px 12px';
          el.style.background = getComputedStyle(th).backgroundColor || '#0f62fe';
          el.style.color = getComputedStyle(th).color || 'white';
          el.style.fontWeight = getComputedStyle(th).fontWeight || '700';
          el.style.fontSize = getComputedStyle(th).fontSize || '12px';
          el.style.borderRadius = '6px';
          el.style.whiteSpace = 'nowrap';
          el.style.boxShadow = '0 6px 18px rgba(0,0,0,0.18)';
          document.body.appendChild(el);
          // Center the ghost relative to the cursor.
          const rect = el.getBoundingClientRect();
          event.dataTransfer.setDragImage(el, rect.width / 2, rect.height / 2);
          this.dragImageEl = el;
        }
      }
    } catch {
      // ignore
    }
    event.stopPropagation();
  }

  onColumnDragOver(overColumnKey: string, event: DragEvent): void {
    if (!this.columnReorderable) return;
    // Allow drop
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';

    const dragKey = this.draggingColumnKey || (event.dataTransfer?.getData('text/plain') ?? '');
    if (!dragKey || !overColumnKey || dragKey === overColumnKey) {
      event.stopPropagation();
      return;
    }

    // Live preview: reorder as you drag over headers so it feels like moving the whole column.
    if (this.columnDragLastOverKey !== overColumnKey && this.columnOrderKeys?.includes(dragKey)) {
      this.columnDragLastOverKey = overColumnKey;
      this.reorderColumns(dragKey, overColumnKey);
      this.scheduleMarkForCheck();
    }

    event.stopPropagation();
  }

  onColumnDrop(dropColumnKey: string, event: DragEvent): void {
    if (!this.columnReorderable) return;
    event.preventDefault();
    const dragKey = this.draggingColumnKey || (event.dataTransfer?.getData('text/plain') ?? '');
    if (!dragKey || !dropColumnKey || dragKey === dropColumnKey) {
      this.draggingColumnKey = null;
      this.columnReorderDragInProgress = false;
      this.restoreBodyDragStyles();
      this.cleanupDragImage();
      this.columnDragLastOverKey = null;
      return;
    }
    // Do not call reorderColumns here: dragover already moved the column into place (live preview).
    // A second reorder on drop shifted the column one slot off from where the user released.
    this.draggingColumnKey = null;
    this.columnReorderDragInProgress = false;
    this.columnDragLastOverKey = null;
    this.restoreBodyDragStyles();
    this.cleanupDragImage();
    this.cdr.markForCheck();
    event.stopPropagation();
  }

  onColumnDragEnd(): void {
    this.draggingColumnKey = null;
    this.columnReorderDragInProgress = false;
    this.columnDragLastOverKey = null;
    this.restoreBodyDragStyles();
    this.cleanupDragImage();
  }

  private reorderColumns(dragKey: string, dropKey: string): void {
    if (!this.columnOrderKeys) return;
    const from = this.columnOrderKeys.indexOf(dragKey);
    const to = this.columnOrderKeys.indexOf(dropKey);
    if (from < 0 || to < 0 || from === to) return;

    const keys = [...this.columnOrderKeys];
    const [removed] = keys.splice(from, 1);
    // After removing index `from`, insert at `to`:
    // - from < to: `to` is the insertion index *after* the column you dropped on (matches “release on this header”).
    // - from > to: `to` still points at that column; insert there = before it.
    // Using (to - 1) when from < to shifted the result one column left of user expectation.
    keys.splice(to, 0, removed);
    this.columnOrderKeys = keys;
  }

  private scheduleMarkForCheck(): void {
    if (this.pendingMarkForCheck) return;
    this.pendingMarkForCheck = true;
    requestAnimationFrame(() => {
      this.pendingMarkForCheck = false;
      this.cdr.markForCheck();
    });
  }

  private restoreBodyDragStyles(): void {
    try {
      const body = document.body;
      if (this.savedBodyCursor != null) body.style.cursor = this.savedBodyCursor;
      if (this.savedBodyUserSelect != null) body.style.userSelect = this.savedBodyUserSelect;
      // Reset saved values so we don't restore stale ones after later drags.
      this.savedBodyCursor = null;
      this.savedBodyUserSelect = null;
    } catch {
      // ignore
    }
  }

  private cleanupDragImage(): void {
    try {
      if (this.dragImageEl && this.dragImageEl.parentNode) {
        this.dragImageEl.parentNode.removeChild(this.dragImageEl);
      }
    } catch {
      // ignore
    } finally {
      this.dragImageEl = null;
    }
  }

  selectAllColumnFilter(column: TableColumn, $event: Event): void {
    $event.stopPropagation();
    const values = this.openColumnFilterUniqueValues.length > 0 && this.columnFilterOpen === column.key
      ? [...this.openColumnFilterUniqueValues]
      : this.getUniqueValuesForColumn(column);
    this.columnFilters[column.key] = new Set(values);
    this._sortedCache = null;
    this._filteredDisplayCacheDataRef = null;
    this._filteredDisplayCacheKey = null;
    this._filteredDisplayCache = null;
    this.schedulePersistHeaderFilterState();
    this.afterColumnFilterMutation();
  }

  clearColumnFilter(column: TableColumn, $event?: Event): void {
    if ($event) $event.stopPropagation();
    delete this.columnFilters[column.key];
    this._sortedCache = null;
    this._filteredDisplayCacheDataRef = null;
    this._filteredDisplayCacheKey = null;
    this._filteredDisplayCache = null;
    this.columnFilterOpen = null;
    this.openColumnFilterUniqueValues = [];
    this.schedulePersistHeaderFilterState();
    this.afterColumnFilterMutation();
  }

  /** Clears every column header filter (when filter panel reset bumps `headerFiltersResetKey`). */
  private clearAllColumnFilters(): void {
    this.columnFilters = {};
    this.columnFilterOpen = null;
    this.openColumnFilterUniqueValues = [];
    this._sortedCache = null;
    this._filteredDisplayCacheDataRef = null;
    this._filteredDisplayCacheKey = null;
    this._filteredDisplayCache = null;
    this.clearPersistedHeaderFilterState();
    this.afterColumnFilterMutation();
  }

  /**
   * Rows used for render/virtual slice (paginated client-side vs full sorted/filtered data).
   */
  private getEffectiveRenderSource(): any[] {
    const usePaginated = !this.isServerSidePagination && (
      (this.config && typeof this.config.pageSize === 'number' && this.config.pageSize > 0) ||
      this.pagination ||
      this.showPagination
    );
    return usePaginated ? (this.paginatedData || []) : (this.sortedData || this.displayData || []);
  }

  /** Stable array reference for virtual slicing (scroll runs outside Angular zone). */
  private getVirtualRenderSource(): any[] {
    const source = this.getEffectiveRenderSource() || [];
    if (source !== this._virtualRenderSourceRef) {
      this._virtualRenderSourceRef = source;
      this._virtualRenderSource = source;
      this.resetVirtualSliceCache();
    }
    return this._virtualRenderSource;
  }

  /**
   * Virtualization follows the full dataset size, not the filtered row count.
   * Keeps virtual mode on while header filters narrow rows so clearing filters does not rebuild the entire DOM.
   */
  private recomputeVirtualEnabled(): void {
    const fullLength = (this.displayData || []).length;
    const configPageSize = this.config && typeof this.config.pageSize === 'number' && this.config.pageSize > 0;
    const paginationActiveClientSide = !this.isServerSidePagination && (configPageSize || this.pagination);
    this.virtualEnabled =
      this.virtualize &&
      !paginationActiveClientSide &&
      fullLength >= this.virtualThreshold;
    if (this.rowReorderable) {
      this.virtualEnabled = false;
      this.visibleRows = [];
      this.topPadding = 0;
      this.bottomPadding = 0;
    } else if (!this.virtualEnabled) {
      this.visibleRows = [];
      this.topPadding = 0;
      this.bottomPadding = 0;
    }
  }

  /** When virtualized, header filter changes must recompute visibleRows immediately (otherwise UI may refresh only after next scroll). */
  private refreshVirtualRangeAfterFilterChange(defer = true): void {
    const apply = () => {
      try {
        this.recomputeVirtualEnabled();
        const el = this.virtualScrollEl ?? (this.tableContainer?.nativeElement as HTMLElement | undefined);
        const scrollTop = el?.scrollTop ?? 0;
        if (this.virtualEnabled) {
          this.updateVirtualRange(scrollTop);
        }
        // Force a tiny flush so the row list updates immediately (avoids requiring user scroll/click).
        this.cdr.detectChanges();
      } catch {
        try { this.cdr.markForCheck(); } catch {}
      }
    };
    if (defer && typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(apply);
    } else {
      apply();
    }
  }

  private emitFilteredRowCount(): void {
    const data = this.displayData || [];
    const hasFilters = this.hasActiveColumnFilters();
    const run = () => {
      let filtered: any[];
      let count: number;
      if (!hasFilters) {
        // Fast path when clearing filters — avoid O(n) scan over the full dataset.
        filtered = data;
        count = data.length;
      } else {
        filtered = this.filteredDisplayData ?? [];
        count = filtered.length;
      }
      this._cachedFilteredDisplayData = filtered;
      this._footerSumCache = null;
      this.filteredRowCountChange.emit(count);
      // Emit null (not the full dataset) when unfiltered to keep unselect responsive on large pages.
      this.filteredRowsChange.emit(hasFilters && count !== data.length ? filtered : (null as any));
    };

    const len = data.length;
    if (len >= 200 && hasFilters) {
      this._emitFilteredToken++;
      const token = this._emitFilteredToken;
      if (this._emitFilteredRaf) cancelAnimationFrame(this._emitFilteredRaf);
      this._emitFilteredRaf = requestAnimationFrame(() => {
        this._emitFilteredRaf = 0;
        if (token !== this._emitFilteredToken) return;
        run();
        this.cdr.markForCheck();
      });
      return;
    }
    run();
  }

  /**
   * Remove stale column-filter values that are not present in the latest display data.
   * This prevents a persisted/stale filter from hiding all rows after fresh API results arrive.
   */
  private pruneColumnFiltersForCurrentData(): void {
    const data = this.displayData || [];
    if (!this.columnFilters || Object.keys(this.columnFilters).length === 0) {
      return;
    }
    // No rows: clear header filters so the next populated dataset is not hidden by stale selections.
    if (!data.length) {
      this.columnFilters = {};
      this._sortedCache = null;
      this._filteredDisplayCacheDataRef = null;
      this._filteredDisplayCacheKey = null;
      this._filteredDisplayCache = null;
      this.persistHeaderFilterState();
      this.cdr.markForCheck();
      return;
    }

    let changed = false;
    const nextFilters: Record<string, Set<string>> = {};

    Object.keys(this.columnFilters).forEach((columnKey) => {
      const column = this.displayColumns.find(c => c.key === columnKey);
      if (!column) {
        changed = true;
        return;
      }

      const available = new Set<string>();
      for (const row of data) {
        const value = this.getCellValueForFilter(row, column);
        if (value !== '') available.add(value);
      }

      const selected = this.columnFilters[columnKey];
      const retained = new Set<string>();
      selected.forEach((value) => {
        if (available.has(value)) retained.add(value);
      });

      if (retained.size > 0) {
        nextFilters[columnKey] = retained;
      } else {
        changed = true;
      }

      if (retained.size !== selected.size) {
        changed = true;
      }
    });

    if (changed) {
      this.columnFilters = nextFilters;
      this._sortedCache = null;
      this.persistHeaderFilterState();
      this.cdr.markForCheck();
    }
  }

  openColumnFilter(columnKey: string, $event: Event): void {
    $event.stopPropagation();
    // Anchor to the filter button so dropdown can be positioned as fixed overlay (not clipped by overflow containers).
    this.columnFilterAnchorEl = ($event.currentTarget as HTMLElement) || null;
    const nextOpen = this.columnFilterOpen === columnKey ? null : columnKey;
    this.columnFilterOpen = nextOpen;
    if (nextOpen) {
      const column = this.displayColumns.find(c => c.key === nextOpen);
      // Single O(n) scan when opening — do NOT call from template *ngFor (that re-ran every change-detection).
      this.openColumnFilterUniqueValues = column ? this.getUniqueValuesForColumn(column) : [];
      // Create portal in DOM before measuring anchor / attaching to body.
      this.cdr.detectChanges();
      this.attachColumnFilterPortalToBody();
      this.repositionColumnFilterDropdown();
      this.attachColumnFilterRepositionListeners();
    } else {
      this.openColumnFilterUniqueValues = [];
      this.detachColumnFilterRepositionListeners();
      this.columnFilterDropdownStyle = null;
      this.columnFilterAnchorEl = null;
    }
    this.cdr.markForCheck();
  }

  closeColumnFilter(): void {
    this.columnFilterOpen = null;
    this.openColumnFilterUniqueValues = [];
    this.detachColumnFilterRepositionListeners();
    this.columnFilterDropdownStyle = null;
    this.columnFilterAnchorEl = null;
    this.cdr.markForCheck();
  }

  trackByFilterValue(_index: number, val: string): string {
    return val;
  }

  get openColumnFilterColumn(): TableColumn | null {
    if (!this.columnFilterOpen) {
      return null;
    }
    return this.displayColumns.find(c => c.key === this.columnFilterOpen) ?? null;
  }

  /** Move filter popover to body so `position:fixed` aligns with viewport (CSS containment on scroll parents breaks in-table fixed). */
  private attachColumnFilterPortalToBody(): void {
    const el = this.columnFilterPortal?.nativeElement;
    if (!el || el.parentElement === document.body) {
      return;
    }
    document.body.appendChild(el);
  }

  private detachColumnFilterPortalFromBody(): void {
    const el = this.columnFilterPortal?.nativeElement;
    if (!el || el.parentElement !== document.body) {
      return;
    }
    try {
      el.remove();
    } catch {
      /* ignore */
    }
  }

  private repositionColumnFilterDropdown(): void {
    const anchor = this.columnFilterAnchorEl;
    if (!anchor) return;

    const portal = this.columnFilterPortal?.nativeElement ?? null;
    const rect = anchor.getBoundingClientRect();
    const minWidth = 160;
    const preferredMaxHeight = 280;
    const margin = 8;

    const onBody = portal?.parentElement === document.body;
    let position: 'fixed' | 'absolute' = onBody ? 'fixed' : 'absolute';
    let left = rect.left;
    let top = rect.bottom + 4;

    if (!onBody && portal) {
      const offsetParent = portal.offsetParent as HTMLElement | null;
      if (offsetParent) {
        const parentRect = offsetParent.getBoundingClientRect();
        left = rect.left - parentRect.left;
        top = rect.bottom - parentRect.top + 4;
      }
    }

    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;

    if (onBody) {
      if (left + minWidth + margin > vw) {
        left = Math.max(margin, vw - minWidth - margin);
      }
      left = Math.max(margin, left);
    } else if (portal?.offsetParent instanceof HTMLElement) {
      const parent = portal.offsetParent;
      left = Math.max(margin, left);
      const maxLeft = Math.max(margin, parent.clientWidth - minWidth - margin);
      if (left > maxLeft) {
        left = maxLeft;
      }
    } else {
      left = Math.max(margin, left);
    }

    let maxHeight = preferredMaxHeight;
    if (onBody) {
      const availableBelow = Math.max(120, Math.floor(vh - top - margin));
      maxHeight = Math.min(preferredMaxHeight, availableBelow);
    } else if (portal?.offsetParent instanceof HTMLElement) {
      const parent = portal.offsetParent;
      const availableBelow = Math.max(120, Math.floor(parent.clientHeight - top - margin));
      maxHeight = Math.min(preferredMaxHeight, availableBelow);
    }

    this.columnFilterDropdownStyle = {
      position,
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      minWidth: `${minWidth}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
      zIndex: '100020'
    };
  }

  private attachColumnFilterRepositionListeners(): void {
    if (this.removeRepositionListeners) return;
    const onAnyScroll = () => {
      if (!this.columnFilterOpen) return;
      this.repositionColumnFilterDropdown();
      this.cdr.markForCheck();
    };
    const onResize = () => {
      if (!this.columnFilterOpen) return;
      this.repositionColumnFilterDropdown();
      this.cdr.markForCheck();
    };

    // Capture scroll anywhere (nested scroll containers).
    document.addEventListener('scroll', onAnyScroll, true);
    window.addEventListener('resize', onResize, { passive: true } as any);
    this.removeRepositionListeners = () => {
      document.removeEventListener('scroll', onAnyScroll, true);
      window.removeEventListener('resize', onResize as any);
    };
  }

  private detachColumnFilterRepositionListeners(): void {
    try { this.removeRepositionListeners?.(); } catch {}
    this.removeRepositionListeners = null;
  }

  private readonly onDocumentClickCapture = (event: MouseEvent) => this.onDocumentClick(event);

  onDocumentClick(event: MouseEvent): void {
    if (!this.columnFilterOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('.column-filter-dropdown')) {
      return;
    }
    if (target?.closest?.('.column-filter-trigger')) {
      return;
    }
    this.closeColumnFilter();
  }

  /**
   * Get background color for a cell. When getCellStyle is provided (e.g. from Daily Roster for patientStatusColor),
   * use it for the Account# column; otherwise default accountNumber column to white.
   */
  getCellBackgroundColor(row: any, column: TableColumn): string | undefined {
    if (this.getCellStyle) {
      const style = this.getCellStyle(row, column);
      if (style?.['backgroundColor']) return style['backgroundColor'];
      if (style?.['background']) return style['background'];
    }
    if (column.key === 'accountNumber' || column.key === 'accountNo' || column.key === 'account') {
      // Default: white Account column (e.g. Daily Roster status stripe). When `rowStyleResolver`
      // supplies a row tint (ERA File Processing), keep Account cells aligned with the rest of the row.
      const precomputedRowStyle = row?._rowStyle;
      if (precomputedRowStyle) {
        const fromRow = precomputedRowStyle['background-color'] ?? precomputedRowStyle['backgroundColor'];
        if (typeof fromRow === 'string' && fromRow.trim()) {
          return fromRow.trim();
        }
        return undefined;
      }
      if (this.rowStyleResolver) {
        const rowStyle = this.rowStyleResolver(row);
        const fromRow = rowStyle?.['background-color'] ?? rowStyle?.['backgroundColor'];
        if (typeof fromRow === 'string' && fromRow.trim()) {
          return fromRow.trim();
        }
        return undefined;
      }
      return '#ffffff';
    }
    return undefined;
  }

  /** Get text color from getCellStyle when provided (e.g. white on black background). */
  getCellTextColor(row: any, column: TableColumn): string | undefined {
    if (this.getCellStyle) {
      const style = this.getCellStyle(row, column);
      return style?.['color'];
    }
    return undefined;
  }

  /**
   * Check if a status has a background color defined.
   */
  private hasStatusBackgroundColor(status: string): boolean {
    const normalizedStatus = status.toLowerCase().trim();
    return normalizedStatus === 'schedule' ||
           normalizedStatus === 'reschedule' ||
           normalizedStatus === 'checkin' ||
           normalizedStatus === 'checkin in' ||
           normalizedStatus === 'in process' ||
           normalizedStatus === 'ready for doctor' ||
           normalizedStatus === 'seen' ||
           normalizedStatus === 'billed' ||
           normalizedStatus === 'cancelled' ||
           normalizedStatus === 'checked out' ||
           normalizedStatus === 'no show' ||
           normalizedStatus === 'not seen' ||
           normalizedStatus === 'not-seen';
  }

  /**
   * Get text color class for a cell based on row selection and status.
   * For selected rows with status colors, use white text for better contrast.
   * Exception: Check-In and In Process statuses use dark blue text when selected.
   * For selected rows without status colors, use primary text on light background.
   */
  /** Tailwind classes applied to the inner `<span>` around cell text (separate from td color). */
  getCellSpanExtraClass(row: any, column: TableColumn): string {
    const extra = column.cellClass?.(row, column);
    return extra != null && String(extra).trim() !== '' ? String(extra).trim() : '';
  }

  getFooterSpanExtraClass(column: TableColumn): string {
    if (!this.footerRow || !column.footerCellClass) return '';
    const raw = this.footerRow[column.key];
    const extra = column.footerCellClass(column, raw);
    return extra != null && String(extra).trim() !== '' ? String(extra).trim() : '';
  }

  getCellTextColorClass(row: any, column: TableColumn): string {
    const isSelected = row?.__isSelected === true || (this.isSelectable && this.selectedRows.has(row));
    const status = (row?.status?.toLowerCase() || '').trim().replace(/\s+/g, ' ');
    if (isSelected && this.selectedRowStatusAwareText) {
      return this.getSelectedStatusTextClass(status);
    }
    if (isSelected && this.selectedRowTextColorClass) {
      return this.selectedRowTextColorClass;
    }

    // Check if this status has a background color
    const hasStatusColor = this.hasStatusBackgroundColor(status);

    // If a row has a status-colored background (including red), ensure readable contrast
    // even when the row is NOT selected.
    if (this.useStatusContrastText && hasStatusColor) {
      const normalizedStatus = status.toLowerCase();
      if (normalizedStatus === 'checkin' || normalizedStatus === 'checkin in' ||
          normalizedStatus === 'check-in' || normalizedStatus === 'check in' ||
          normalizedStatus === 'in process' || normalizedStatus === 'in-process' || normalizedStatus === 'inprocess') {
        return 'text-[#01579b]'; // Dark blue for check-in/in-process
      }
      // For red/dark-ish status backgrounds, use slightly-off-white for clarity.
      // Keep identical regardless of selection state.
      return 'text-slate-50';
    }

    // Base text color for rows without status background
    return this.bodyTextColorClass || 'text-slate-800';
  }

  /**
   * Get text color class for actions column based on row selection and status.
   * For selected rows with status colors, use white text for better contrast.
   * Exception: Check-In and In Process statuses use dark blue text when selected.
   * For selected rows without status colors, use primary text on light background.
   */
  getActionsColumnTextColorClass(row: any): string {
    const isSelected = row?.__isSelected === true || (this.isSelectable && this.selectedRows.has(row));
    const status = (row?.status?.toLowerCase() || '').trim().replace(/\s+/g, ' ');
    if (isSelected && this.selectedRowStatusAwareText) {
      return this.getSelectedStatusTextClass(status);
    }
    if (isSelected && this.selectedRowTextColorClass) {
      return this.selectedRowTextColorClass;
    }

    // Check if this status has a background color
    const hasStatusColor = this.hasStatusBackgroundColor(status);

    // If a row has a status-colored background (including red), ensure readable contrast
    // even when the row is NOT selected.
    if (this.useStatusContrastText && hasStatusColor) {
      const normalizedStatus = status.toLowerCase();
      if (normalizedStatus === 'checkin' || normalizedStatus === 'checkin in' ||
          normalizedStatus === 'check-in' || normalizedStatus === 'check in' ||
          normalizedStatus === 'in process' || normalizedStatus === 'in-process' || normalizedStatus === 'inprocess') {
        return 'text-[#01579b]'; // Dark blue for check-in/in-process
      }
      return 'text-slate-50';
    }

    return this.bodyTextColorClass || 'text-slate-800';
  }

  /** Selected-row status contrast: keep white only on dark statuses, use dark text for others. */
  getSelectedStatusTextClass(status: string): string {
    if (!this.selectedRowStatusAwareText) {
      return this.selectedRowTextColorClass || 'text-white';
    }
    const s = (status || '').toLowerCase().trim().replace(/\s+/g, ' ');
    if (
      s === 'schedule' ||
      s === 'reschedule' ||
      s === 'cancelled' ||
      s === 'not seen' ||
      s === 'not-seen'
    ) {
      return 'text-white !font-bold';
    }
    return 'text-[#0f172a] !font-bold';
  }

  onSort(column: TableColumn): void {
    if (!column.sortable) return;

    // Three-state sorting: asc → desc → reset (null)
    if (this.sortColumn === column.key) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortDirection = null; // Reset to default
        this.sortColumn = '';
      }
    } else {
      this.sortColumn = column.key;
      this.sortDirection = 'asc';
    }

    // Reset to first page when sorting changes (if pagination is active)
    if (this.showPagination && !this.isServerSidePagination) {
      this.currentPage = 1;
    }

    // Clear cache when sort changes to force recomputation
    this._sortedCache = null;
    this._lastDataRef = null;
    this._lastSortColumn = '';
    this._lastSortDirection = null;
    this.persistHeaderFilterState();
    
    // Force change detection to update the view
    this.cdr.markForCheck();
    // Virtualized tables only refresh `visibleRows` on scroll or data input changes; re-slice after sort (same as header filters).
    this.refreshVirtualRangeAfterFilterChange();

    // Emit sort event - use empty column string to indicate reset (direction will be 'asc' but ignored)
    if (this.sortDirection !== null) {
      this.sort.emit({ column: this.sortColumn, direction: this.sortDirection });
    } else {
      // When resetting, emit empty column to indicate no active sort
      // Components can check if column is empty to handle reset
      this.sort.emit({ column: '', direction: 'asc' });
    }
  }

  private persistHeaderFilterState(): void {
    if (!this.headerFiltersStorageKey) return;
    try {
      const filters: Record<string, string[]> = {};
      Object.keys(this.columnFilters || {}).forEach((key) => {
        const selected = this.columnFilters[key];
        if (selected && selected.size > 0) {
          filters[key] = Array.from(selected);
        }
      });
      localStorage.setItem(this.headerFiltersStorageKey, JSON.stringify({
        columnFilters: filters,
        sortColumn: this.sortColumn || '',
        sortDirection: this.sortDirection
      }));
    } catch {
      // Ignore storage failures.
    }
  }

  private restoreHeaderFilterState(): void {
    if (!this.headerFiltersStorageKey) return;
    try {
      const raw = localStorage.getItem(this.headerFiltersStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const restored: Record<string, Set<string>> = {};
      const fromStorage = parsed?.columnFilters || {};
      Object.keys(fromStorage).forEach((key) => {
        const values = Array.isArray(fromStorage[key]) ? fromStorage[key] : [];
        if (values.length > 0) restored[key] = new Set(values.map((v: unknown) => String(v)));
      });
      this.columnFilters = restored;
      this.sortColumn = typeof parsed?.sortColumn === 'string' ? parsed.sortColumn : '';
      this.sortDirection = parsed?.sortDirection === 'asc' || parsed?.sortDirection === 'desc'
        ? parsed.sortDirection
        : null;
      this._sortedCache = null;
      this.emitFilteredRowCount();
      this.cdr.markForCheck();
    } catch {
      this.clearPersistedHeaderFilterState();
    }
  }

  private clearPersistedHeaderFilterState(): void {
    if (!this.headerFiltersStorageKey) return;
    try {
      localStorage.removeItem(this.headerFiltersStorageKey);
    } catch {
      // Ignore storage failures.
    }
  }

  onRowClick(row: any, event?: MouseEvent): void {
    if (this.isSelectable) {
      const t = event?.target as HTMLElement | null;
      if (t?.closest('input[type="checkbox"]')) {
        this.rowClick.emit(row);
        return;
      }
      if (this.selectedRows.has(row)) {
        this.selectedRows.delete(row);
      } else {
        this.selectedRows.add(row);
      }
      this.emitSelection();
    }
    if (this.rowClickIncludesEvent) {
      this.rowClick.emit({ row, event });
    } else {
      this.rowClick.emit(row);
    }
  }

  onRowDblclick(row: any): void {
    this.rowDblclick.emit(row);
  }

  getFooterCellValue(column: TableColumn): string | number {
    const v = this.footerRow?.[column.key];
    if (v === undefined || v === null || v === '') return '';

    const footerNumeric = this.tryParseNumeric(v);
    const isMoneyColumn = this.shouldFormatAsMoney(column, v);
    const hasActiveHeaderFilters = this.hasActiveColumnFilters();

    // When user applies column header filters, numeric footer values must reflect filtered rows only.
    if (hasActiveHeaderFilters && footerNumeric !== null) {
      const sum = this.getNumericFooterSum(column.key);
      return this.formatFooterNumber(sum, isMoneyColumn);
    }

    if (typeof v === 'number') return isNaN(v) ? '0' : this.formatFooterNumber(v, isMoneyColumn);
    if (typeof v === 'string' && v.trim() === '') return '';
    return v;
  }

  private formatFooterNumber(value: number, forceMoneyFormat: boolean = false): string {
    const n = Number(value);
    if (isNaN(n)) return '0';
    if (forceMoneyFormat) return this.formatMoneyDisplay(n);
    // Preserve legacy footer behavior for non-money numeric columns.
    if (Number.isInteger(n)) return String(n);
    return this.formatAccountingFixed2(n);
  }

  /** Two decimals; negatives as (50.00) for footer non-locale amounts. */
  private formatAccountingFixed2(n: number): string {
    const abs = Math.abs(n);
    const s = abs.toFixed(2);
    return n < 0 ? `(${s})` : s;
  }

  private formatMoneyDisplay(value: any): string {
    const n = this.tryParseNumeric(value);
    if (n === null) return String(value ?? '');
    return this.formatMoneyDisplayRaw(n);
  }

  /** Full footer text for `title` when truncated. */
  footerCellTooltip(column: TableColumn): string | null {
    const v = this.getFooterCellValue(column);
    if (v === '' || v == null) return null;
    const s = String(v).trim();
    return s.length > 0 ? s : null;
  }

  isFooterCellNumeric(column: TableColumn): boolean {
    const v = this.footerRow?.[column.key];
    return this.tryParseNumeric(v) !== null;
  }

  getFooterCellAlign(column: TableColumn): string {
    if (this.isFooterCellNumeric(column)) return 'center';
    return this.getResolvedColumnAlign(column);
  }

  /** True when footer has a value for this column (used for grey-bordered total cells). */
  hasFooterCellValue(column: TableColumn): boolean {
    const v = this.footerRow?.[column.key];
    if (v === undefined || v === null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    return true;
  }

  private hasActiveColumnFilters(): boolean {
    const keys = Object.keys(this.columnFilters || {});
    return keys.some((k) => (this.columnFilters?.[k]?.size ?? 0) > 0);
  }

  /** Attempts to parse numeric values stored as numbers or formatted strings (e.g. `$1,234.56`). */
  private tryParseNumeric(value: any): number | null {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return isNaN(value) ? null : value;
    if (typeof value !== 'string') return null;

    const s = value.trim();
    if (!s) return null;

    const cleaned = s.replace(/[^0-9.\-]/g, '');
    if (!cleaned) return null;

    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  /** True for amount/price style columns where values should render with thousand separators. */
  private shouldFormatAsMoney(column: TableColumn, value: any): boolean {
    if (value === null || value === undefined || value === '') return false;
    const key = String(column?.key || '').toLowerCase();
    // datePaid and similar — key contains "paid" but is a date column.
    if (key.startsWith('date') || key.endsWith('date')) return false;
    if (this.tryParseNumeric(value) === null) return false;
    const label = String(column?.label || '').toLowerCase();
    const moneyHints = [
      'amount', 'amt', 'fee', 'paid', 'balance', 'bal', 'payment', 'pay',
      'copay', 'co pay', 'discount', 'write off', 'writeoff', 'total', 'charge'
    ];
    return moneyHints.some((h) => key.includes(h.replace(/\s+/g, '')) || label.includes(h));
  }

  /** Format numeric text with thousand separators and fixed 2 decimals; negatives as (1,234.56). */
  private formatMoneyDisplayRaw(n: number): string {
    const abs = Math.abs(n);
    const formatted = abs.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return n < 0 ? `(${formatted})` : formatted;
  }

  /** Sum a given numeric column key across currently filtered rows (header filters). */
  private getNumericFooterSum(columnKey: string): number {
    if (!this._footerSumCache) this._footerSumCache = {};
    if (this._footerSumCache[columnKey] !== undefined) return this._footerSumCache[columnKey];

    const rows = this._cachedFilteredDisplayData ?? this.filteredDisplayData ?? [];
    const sum = rows.reduce((acc, row) => {
      const n = this.tryParseNumeric(row?.[columnKey]);
      return acc + (n ?? 0);
    }, 0);

    this._footerSumCache[columnKey] = sum;
    return sum;
  }

  onSelectAll(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.paginatedData.forEach(row => this.selectedRows.add(row));
    } else {
      this.paginatedData.forEach(row => this.selectedRows.delete(row));
    }
    this.emitSelection();
  }

  onSelectRow(event: Event, row: any): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.selectedRows.add(row);
    } else {
      this.selectedRows.delete(row);
    }
    this.emitSelection();
  }

  isRowSelected(row: any): boolean {
    return this.selectedRows.has(row) || row?.__isSelected === true;
  }

  isAllSelected(): boolean {
    return this.paginatedData.length > 0 &&
      this.paginatedData.every(row => this.selectedRows.has(row));
  }

  isIndeterminate(): boolean {
    const selectedCount = this.paginatedData.filter(row => this.selectedRows.has(row)).length;
    return selectedCount > 0 && selectedCount < this.paginatedData.length;
  }

  private emitSelection(): void {
    this.rowSelect.emit(Array.from(this.selectedRows));
  }

  /** Clear checkbox multi-select (e.g. after ERA post highlights posted rows). */
  clearRowSelection(): void {
    if (this.selectedRows.size === 0) {
      return;
    }
    this.selectedRows.clear();
    this.emitSelection();
    this.cdr.markForCheck();
  }

  /** Replace checkbox selection with explicit row refs (e.g. after ERA post replaces row objects). */
  setSelectedRows(rows: any[]): void {
    this.selectedRows.clear();
    for (const row of rows ?? []) {
      if (row != null) {
        this.selectedRows.add(row);
      }
    }
    this.emitSelection();
    this.cdr.markForCheck();
  }

  private onContainerScroll(): void {
    if (!this.virtualEnabled || !this.tableContainer) return;
    const el = this.virtualScrollEl ?? (this.tableContainer.nativeElement as HTMLElement);
    // Scroll listener is outside Angular zone — detectChanges when the slice moves so rows paint immediately.
    if (this.updateVirtualRange(el.scrollTop)) {
      this.cdr.detectChanges();
    }
  }

  private resetVirtualSliceCache(): void {
    this._lastVirtualSliceStart = -1;
    this._lastVirtualSliceEnd = -1;
    this._lastVirtualTopPadding = -1;
    this._lastVirtualBottomPadding = -1;
  }

  private applyVirtualSlice(start: number, end: number, topPadding: number, bottomPadding: number, source: any[]): boolean {
    if (
      start === this._lastVirtualSliceStart &&
      end === this._lastVirtualSliceEnd &&
      topPadding === this._lastVirtualTopPadding &&
      bottomPadding === this._lastVirtualBottomPadding
    ) {
      return false;
    }
    this._lastVirtualSliceStart = start;
    this._lastVirtualSliceEnd = end;
    this._lastVirtualTopPadding = topPadding;
    this._lastVirtualBottomPadding = bottomPadding;
    this.visibleRows = this.sliceVirtualRowsWithAnchors(source, start, end);
    this.topPadding = topPadding;
    this.bottomPadding = bottomPadding;
    return true;
  }

  private getVirtualRowHeightPx(): number {
    const h = this.virtualRowHeight ?? this.rowHeight;
    return Number.isFinite(h) && h > 0 ? h : this.rowHeight;
  }

  /**
   * When virtualizing group-band tables, ensure a visible slice that starts inside a group
   * still includes that group's anchor (full-width header) row.
   */
  private isGroupTrailerRow(row: any): boolean {
    return row?.rowType === 'subtotal' || row?.rowKind === 'subtotal';
  }

  private sliceVirtualRowsWithAnchors(source: any[], start: number, end: number): any[] {
    const boundedEnd = Math.min(end, source.length);
    let slice = source.slice(start, boundedEnd);
    if (slice.length === 0) {
      return slice;
    }

    if (this.isFullWidthRow && start > 0 && !this.isFullWidthRow(source[start])) {
      for (let i = start - 1; i >= 0; i--) {
        if (this.isFullWidthRow(source[i])) {
          slice.unshift(source[i]);
          break;
        }
        if (this.isGroupTrailerRow(source[i])) {
          break;
        }
      }
    }

    if (boundedEnd < source.length && this.isGroupTrailerRow(source[boundedEnd])) {
      const last = slice[slice.length - 1];
      const isAnchor = this.isFullWidthRow?.(last) === true;
      if (last && !isAnchor && !this.isGroupTrailerRow(last)) {
        slice.push(source[boundedEnd]);
      }
    }

    return slice;
  }

  private updateVirtualRange(scrollTop: number): boolean {
    const container = this.virtualScrollEl ?? (this.tableContainer?.nativeElement as HTMLElement);
    const containerHeight = container ? container.clientHeight : window.innerHeight;
    const rh = this.getVirtualRowHeightPx();
    const rowsInView = Math.ceil(containerHeight / rh);
    const source = this.getVirtualRenderSource();
    const len = source.length;

    if (len === 0) {
      if (
        this.visibleRows.length === 0 &&
        this.topPadding === 0 &&
        this.bottomPadding === 0
      ) {
        return false;
      }
      this.resetVirtualSliceCache();
      this.visibleRows = [];
      this.topPadding = 0;
      this.bottomPadding = 0;
      return true;
    }

    const totalContentHeight = len * rh;
    if (totalContentHeight <= containerHeight) {
      if (
        this.visibleRows === source &&
        this.topPadding === 0 &&
        this.bottomPadding === 0
      ) {
        return false;
      }
      this.resetVirtualSliceCache();
      this.visibleRows = source;
      this.topPadding = 0;
      this.bottomPadding = 0;
      return true;
    }

    const buffer = Number.isFinite(this.virtualBuffer) && this.virtualBuffer >= 0 ? this.virtualBuffer : 10;
    const start = Math.max(0, Math.floor(Math.max(0, scrollTop) / rh) - buffer);
    const end = Math.min(len, start + rowsInView + buffer * 2);
    const topPadding = start * rh;
    const bottomPadding = Math.max(0, (len - end) * rh);
    return this.applyVirtualSlice(start, end, topPadding, bottomPadding, source);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.pageChange.emit(page);
      this.cdr.markForCheck();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  getSortIcon(column: TableColumn): string {
    if (this.sortColumn !== column.key || this.sortDirection === null) {
      return '';
    }
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  getEndIndex(): number {
    return Math.min(this.currentPage * this.displayPageSize, this.displayData.length);
  }

  getPageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  getHeaderRowClass(): string {
    if (this.config?.headerClass) return this.config.headerClass;
    const base = 'bg-primary text-white';
    return this.headerDarkUnderline
      ? `${base} border-b-2 border-slate-700`
      : `${base} border-b border-transparent`;
  }

  getRowClasses(row: any, index: number, isLast: boolean): string {
    const borderClass = this.darkRowBorders ? 'border-b-2 border-slate-600' : 'border-b border-slate-400';
    const baseClasses = `${borderClass} transition-colors`;
    // Don't remove bottom border on last row if there's a footer - it should connect
    const lastClass = isLast && !this.footerRow && !this.footerTemplate ? 'border-b-0' : '';
    const hoverClass = this.isHoverable ? 'hover:opacity-90' : '';

    // Get status from row first
    const status = row?.status?.toLowerCase() || '';
    const isSelected = row?.__isSelected === true || (this.isSelectable && this.selectedRows.has(row));

    let statusBgClass = '';
    let hasStatusColor = false;

    const precomputedRowStyle = row?._rowStyle;
    const hasPrecomputedRowBg =
      !!precomputedRowStyle &&
      !!(precomputedRowStyle['background-color'] ?? precomputedRowStyle['backgroundColor']);

    // Text color is handled at the cell level, not row level
    // This prevents row-level classes from overriding cell-level text colors
    if (hasPrecomputedRowBg) {
      hasStatusColor = true;
    } else switch (status) {
      case 'schedule':
        statusBgClass = 'bg-[rgba(222,21,60,0.8)]';
        hasStatusColor = true;
        break;
      case 'reschedule':
        statusBgClass = 'bg-[rgba(71,153,153,0.8)]';
        hasStatusColor = true;
        break;
      case 'checkin':
      case 'checkin in':
        statusBgClass = 'bg-[rgba(176,226,232,0.8)]';
        hasStatusColor = true;
        break;
      case 'in process':
        statusBgClass = 'bg-[rgba(177,197,225,0.8)]';
        hasStatusColor = true;
        break;
      case 'ready for doctor':
        statusBgClass = 'bg-[rgba(92,255,90,0.8)]';
        hasStatusColor = true;
        break;
      case 'seen':
        statusBgClass = 'bg-[rgba(254,0,252,0.8)]';
        hasStatusColor = true;
        break;
      case 'billed':
        statusBgClass = 'bg-[rgba(101,205,169,0.8)]';
        hasStatusColor = true;
        break;
      case 'cancelled':
        statusBgClass = 'bg-[rgba(251,0,0,0.8)]';
        hasStatusColor = true;
        break;
      case 'checked out':
        statusBgClass = 'bg-[rgba(239,234,169,0.8)]';
        hasStatusColor = true;
        break;
      case 'no show':
        statusBgClass = 'bg-[rgba(255,191,204,0.8)]';
        hasStatusColor = true;
        break;
      case 'not seen':
      case 'not-seen':
        statusBgClass = 'bg-[rgba(79,101,139,0.8)]';
        hasStatusColor = true;
        break;
      default:
        // Default: use striped pattern if enabled, or selected background if row is selected
        if (isSelected) {
          // Light primary tint (approx. #01579b at low opacity) - only for rows without status color
          statusBgClass = 'bg-[rgba(1,87,155,0.18)]';
        } else if (this.isStriped && index % 2 === 1) {
          statusBgClass = 'bg-surface';
        } else {
          statusBgClass = 'bg-white';
        }
    }

    // If row has a status color, always keep it (even when selected)
    // Only apply selected background for rows without status colors
    const extraRowClass = this.config?.rowClassResolver?.(row, index)?.trim() || '';
    return `${baseClasses} ${statusBgClass} ${lastClass} ${hoverClass} ${extraRowClass}`.trim();
  }

  getRowStyle(row: any): { [key: string]: string } {
    const status = row?.status?.toLowerCase() || '';
    const style: { [key: string]: string } = {};

    switch (status) {
      case 'checkin':
        style['background-color'] = 'var(--status-check-in)';
        break;
      case 'in process':
        style['background-color'] = 'var(--status-in-process)';
        break;
      case 'checked out':
        style['background-color'] = 'var(--status-checked-out)';
        break;
      case 'billed':
        style['background-color'] = 'var(--status-billed)';
        break;
      case 'no show':
        style['background-color'] = 'var(--status-no-show)';
        break;
      case 'not-seen':
        style['background-color'] = 'var(--status-not-seen)';
        break;
      case 'cancelled':
        style['background-color'] = 'var(--status-cancelled)';
        break;
      case 'scheduled':
        style['background-color'] = 'var(--status-scheduled)';
        break;
      case 'rescheduled':
        style['background-color'] = 'var(--status-rescheduled)';
        break;
      case 'ready for doctor':
        style['background-color'] = 'var(--status-ready-for-doctor)';
        break;
      case 'seen':
        style['background-color'] = 'var(--status-seen)';
        break;
    }

    return style;
  }

  getResolvedRowStyle(row: any): { [key: string]: string } | undefined {
    const precomputed = row?._rowStyle;
    if (precomputed) {
      return precomputed;
    }
    if (!this.rowStyleResolver) return undefined;
    return this.rowStyleResolver(row);
  }

  shouldShowDollarSign(row: any): boolean {
    const status = row?.status?.toLowerCase() || '';
    return status === 'checkin' || status === 'in process';
  }

  trackByRow(index: number, row: any): any {
    if (row == null) return index;
    if (row.__rowUid != null) {
      return `uid:${row.__rowUid}`;
    }
    if (row.rowType === 'group' && row.groupId != null) {
      return `grp:${row.groupId}`;
    }
    const rowKey = row.__rowKey;
    if (rowKey != null && String(rowKey).trim() !== '') {
      return `rk:${String(rowKey)}`;
    }
    const eraId = row.eraId;
    if (eraId != null && String(eraId).trim() !== '') {
      return `era:${String(eraId)}`;
    }
    const visitKey =
      row.visitNo ??
      row.visitNumber ??
      row.patientVisitId ??
      row.patientVisitId1 ??
      row._apiData?.patientVisitId;
    const acct = row.accountNo ?? row.patientAccountNo ?? row._apiData?.patientAccountNo;
    if (visitKey != null && String(visitKey).trim() !== '') {
      return acct != null && String(acct).trim() !== ''
        ? `v:${String(visitKey)}|a:${String(acct)}`
        : `v:${String(visitKey)}`;
    }
    return row.visitChargeEntryId ?? row.appointmentId ?? row.id ?? index;
  }

  /** Drop Set entries that are no longer in the current row list (e.g. after API refresh replaces row objects). */
  private pruneStaleSelectedRows(): void {
    const rows = this.filteredDisplayData || this.displayData || [];
    const flush = () => {
      Promise.resolve().then(() => {
        this.emitSelection();
        this.cdr.markForCheck();
      });
    };
    if (rows.length === 0) {
      if (this.selectedRows.size > 0) {
        this.selectedRows.clear();
        flush();
      }
      return;
    }
    const set = new Set(rows);
    let removed = false;
    for (const r of Array.from(this.selectedRows)) {
      if (!set.has(r)) {
        this.selectedRows.delete(r);
        removed = true;
      }
    }
    if (removed) {
      flush();
    }
  }

  onActionsMenuClick(event: Event, row: any): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const button = target.closest('button') as HTMLElement;
    if (button) {
      const buttonRect = button.getBoundingClientRect();
      const menuHeight = 200;
      const menuWidth = 180;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Always position menu to the right side of the button (horizontally)
      let x = buttonRect.right + 5;
      let y = buttonRect.top;

      // Check if menu would overflow on the right side
      if (x + menuWidth > viewportWidth) {
        // Try positioning to the left of button
        if (buttonRect.left - menuWidth > 0) {
          x = buttonRect.left - menuWidth - 5;
        } else {
          // If no space on left, align to right edge
          x = viewportWidth - menuWidth - 10;
        }
      }

      // NEVER move menu upward - always keep y at button top or adjust downward only
      // If menu would overflow at bottom, just clip it or adjust slightly downward
      if (y + menuHeight > viewportHeight) {
        // Keep y at button top, but ensure menu doesn't go below viewport
        // Menu will be partially visible or scrollable, but never moves up
        y = Math.min(buttonRect.top, viewportHeight - menuHeight - 10);
        // Ensure y is not negative
        y = Math.max(10, y);
      }

      this.contextMenuOpen = {
        row,
        x,
        y
      };
    }
  }

  onRowRightClick(event: MouseEvent, row: any): void {
    if (this.disableContextMenu) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (this.showActionsColumn && this.actionMenuItems) {
      event.preventDefault();
      event.stopPropagation();

      const menuHeight = 200;
      const menuWidth = 180;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Always position menu to the right side of the click (horizontally)
      let x = event.clientX + 5;
      let y = event.clientY;

      // Check if menu would overflow on the right side
      if (x + menuWidth > viewportWidth) {
        // Try positioning to the left of click
        if (event.clientX - menuWidth > 0) {
          x = event.clientX - menuWidth - 5;
        } else {
          // If no space on left, align to right edge
          x = viewportWidth - menuWidth - 10;
        }
      }

      // NEVER move menu upward - always keep y at click position or adjust downward only
      // If menu would overflow at bottom, just clip it or adjust slightly downward
      if (y + menuHeight > viewportHeight) {
        // Keep y at click position, but ensure menu doesn't go below viewport
        // Menu will be partially visible or scrollable, but never moves up
        y = Math.min(event.clientY, viewportHeight - menuHeight - 10);
        // Ensure y is not negative
        y = Math.max(10, y);
      }

      this.contextMenuOpen = {
        row,
        x,
        y
      };
    }
  }

  onContextMenuAction(action: string, row: any): void {
    if (action === 'view-details' && this.onViewDetails) {
      this.onViewDetails(row);
    } else {
      this.actionClick.emit({ action, row });
    }
    this.contextMenuOpen = null;
  }

  onContextMenuClose(): void {
    this.contextMenuOpen = null;
  }

  onViewClick(event: Event, row: any): void {
    event.stopPropagation();
    if (this.onViewDetails) {
      this.onViewDetails(row);
    }
  }

  onDeleteClick(event: Event, row: any): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.onDelete) {
      this.onDelete(row);
    }
  }

  onHistoryClick(event: Event, row: any): void {
    event.stopPropagation();
    if (this.onHistory) {
      this.onHistory(row);
    }
  }

  getActionMenuItems(row: any): any[] {
    let items: any[] = [];

    if (this.actionMenuItems) {
      const customItems = this.actionMenuItems(row);
      items = [...items, ...customItems];
    }

    return items.map(item => ({
      label: item.label,
      icon: item.icon,
      action: item.action,
      disabled: typeof item.disabled === 'function' ? item.disabled(row) : item.disabled,
      divider: item.divider
    }));
  }

  onRowReorderDragStart(renderIndex: number, event: DragEvent): void {
    if (!this.rowReorderable) return;
    event.stopPropagation();
    this.rowDragSourceRenderIndex = renderIndex;
    this.rowDragOverRenderIndex = null;
    const dt = event.dataTransfer;
    if (dt) {
      dt.effectAllowed = 'move';
      dt.setData('text/plain', String(renderIndex));
      try {
        const t = event.target as HTMLElement | null;
        if (t) dt.setDragImage(t, 12, 12);
      } catch {
        /* ignore */
      }
    }
    this.cdr.markForCheck();
  }

  onRowReorderDragEnd(): void {
    this.rowDragSourceRenderIndex = null;
    this.rowDragOverRenderIndex = null;
    this.cdr.markForCheck();
  }

  onRowReorderDragOver(renderIndex: number, event: DragEvent): void {
    if (!this.rowReorderable || this.rowDragSourceRenderIndex === null) return;
    event.preventDefault();
    const dt = event.dataTransfer;
    if (dt) dt.dropEffect = 'move';
    if (this.rowDragOverRenderIndex !== renderIndex) {
      this.rowDragOverRenderIndex = renderIndex;
      this.cdr.markForCheck();
    }
  }

  onRowReorderDrop(renderIndex: number, event: DragEvent): void {
    if (!this.rowReorderable) return;
    event.preventDefault();
    event.stopPropagation();
    const fromRendered =
      this.rowDragSourceRenderIndex ??
      parseInt(event.dataTransfer?.getData('text/plain') ?? '-1', 10);
    if (fromRendered < 0 || fromRendered === renderIndex) {
      this.onRowReorderDragEnd();
      return;
    }
    const rows = this.renderRows;
    const fromRow = rows[fromRendered];
    const toRow = rows[renderIndex];
    const fromDataIndex = this.displayData.indexOf(fromRow);
    const toDataIndex = this.displayData.indexOf(toRow);
    if (fromDataIndex < 0 || toDataIndex < 0) {
      this.onRowReorderDragEnd();
      return;
    }
    this.rowReorder.emit({ fromIndex: fromDataIndex, toIndex: toDataIndex });
    this.onRowReorderDragEnd();
  }

  rowReorderRowClass(renderIndex: number): string {
    if (!this.rowReorderable) return '';
    // Full-row background only (no rings/borders) so the dragged row reads as clearly “selected”.
    if (this.rowDragSourceRenderIndex === renderIndex) {
      return '!bg-sky-200 relative z-[1] shadow-sm';
    }
    if (this.rowDragOverRenderIndex === renderIndex && this.rowDragSourceRenderIndex !== renderIndex) {
      return '!bg-sky-100';
    }
    return '';
  }

  /**
   * Parents can call after replacing `config.data` asynchronously so OnPush + internal caches show new rows immediately.
   */
  refreshGrid(): void {
    try {
      this._cellValueCache = new WeakMap();
    } catch {
      /* ignore */
    }
    try {
      this._filterValueCache = new WeakMap();
    } catch {
      /* ignore */
    }
    this._sortedCache = null;
    this._lastDataRef = null;
    this._filteredDisplayCacheDataRef = null;
    this._filteredDisplayCacheKey = null;
    this._filteredDisplayCache = null;
    this._uniqueValuesCacheDataRef = null;
    this._uniqueValuesCache.clear();
    this.cdr.markForCheck();
    setTimeout(() => {
      try {
        this.cdr.detectChanges();
      } catch {
        /* ignore */
      }
    }, 0);
  }

  /** Lightweight refresh when only row selection flags change (avoids full cache flush). */
  notifySelectionChange(): void {
    this.cdr.markForCheck();
  }
}
